import styles from '@/styles/press.module.css';

import { useEffect, useRef, useState } from 'react';
import { IconCheck, IconCopy } from '@tabler/icons-react';

interface CopyBlockProps {
  label: string;
  text: string;
  /** Shown next to the label, e.g. a word count. */
  meta?: string;
}

/** A ready-to-paste text (boilerplate, bio) with a copy button that confirms the copy. */
const CopyBlock = ({ label, text, meta }: CopyBlockProps) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article className={styles.copyBlock}>
      <div className={styles.copyHead}>
        <p className={styles.copyLabel}>
          {label}
          {meta && <span>{meta}</span>}
        </p>
        <button type="button" className={styles.copyButton} onClick={copy}>
          {copied ? (
            <IconCheck size={15} aria-hidden="true" />
          ) : (
            <IconCopy size={15} aria-hidden="true" />
          )}
          {copied ? 'Copied' : 'Copy'}
          <span className="sr-only"> {label.toLowerCase()} text</span>
        </button>
      </div>
      <div className={styles.copyText}>
        {text.split('\n\n').map((paragraph) => (
          <p key={paragraph.slice(0, 32)}>{paragraph}</p>
        ))}
      </div>
      <span className="sr-only" aria-live="polite">
        {copied ? `${label} text copied to the clipboard` : ''}
      </span>
    </article>
  );
};

export default CopyBlock;
